import type { BlogPost } from "@shared/schema";

export interface BlogCategory {
  slug: string;
  name: string;
  description: string;
  subcategories: string[];
}

export const BLOG_CATEGORIES: BlogCategory[] = [
  {
    slug: "startup-finance",
    name: "Startup Finance",
    description: "Burn rate, runway, valuation, dilution, and financial planning for founders.",
    subcategories: ["Burn Rate", "Runway", "Valuation", "Dilution"],
  },
  {
    slug: "growth-metrics",
    name: "Growth & SaaS Metrics",
    description: "LTV/CAC, churn, payback period, and the unit economics that drive SaaS growth.",
    subcategories: ["LTV / CAC", "Churn", "Payback Period"],
  },
  {
    slug: "market-research",
    name: "Market Research",
    description: "TAM/SAM/SOM, market sizing methods, industry analysis, and data sources for founders.",
    subcategories: ["TAM / SAM / SOM", "Market Sizing", "Industry Analysis"],
  },
  {
    slug: "founder-strategy",
    name: "Founder Strategy",
    description: "Fundraising, pitch decks, venture capital, compliance, and strategic decision-making.",
    subcategories: ["Fundraising", "Pitch Decks", "Venture Capital"],
  },
];

const STARTUP_FINANCE_SLUGS = new Set([
  "net-burn-vs-gross-burn",
  "runway-by-stage-pre-seed-vs-seed",
  "how-to-extend-runway-without-layoffs",
  "runway-benchmarks-by-arr-band",
  "what-is-startup-burn-rate",
  "gross-burn-vs-net-burn-investors",
  "how-to-calculate-burn-rate-step-by-step",
  "burn-rate-matters-more-than-revenue",
  "burn-rate-benchmarks-by-stage",
  "how-to-calculate-startup-runway-using-burn-rate",
  "12-ways-to-reduce-burn-rate",
  "when-to-start-fundraising-based-on-burn-rate",
  "how-much-runway-should-startups-maintain",
  "burn-rate-vs-runway-investor-metric",
  "burn-multiple-metric-vcs-use",
  "good-burn-rate-venture-backed-startups",
  "how-investors-evaluate-burn-rate-due-diligence",
  "burn-rate-vs-growth-rate",
  "rule-of-40-vs-burn-multiple",
  "startup-budget-template-burn-rate",
  "hiring-plans-burn-rate",
  "forecast-burn-rate-12-months",
  "fixed-vs-variable-costs-startups",
  "when-to-increase-burn-rate-accelerate-growth",
]);

const GROWTH_METRICS_SLUGS = new Set([
  "what-is-ltv-cac-ratio",
  "how-to-calculate-customer-lifetime-value",
  "how-to-calculate-customer-acquisition-cost",
  "why-ltv-cac-most-important-saas-metric",
  "ltv-cac-calculation-mistakes",
  "good-ltv-cac-ratio-saas",
  "improve-ltv-cac-without-raising-prices",
  "ltv-cac-benchmarks-by-stage",
  "relationship-between-churn-and-ltv",
  "why-startups-have-broken-ltv-cac",
  "metrics-venture-capitalists-care-about",
  "saas-magic-number-explained",
  "ltv-cac-vs-payback-period",
  "how-investors-evaluate-unit-economics",
  "ltv-cac-affects-startup-valuation",
  "how-much-startups-spend-acquire-customers",
  "when-cac-is-too-high",
  "paid-ads-vs-organic-growth-cac",
  "retention-improves-ltv-more-than-growth",
  "customer-acquisition-channels-ranked-efficiency",
]);

const MARKET_RESEARCH_SLUGS = new Set([
  "what-are-tam-sam-som",
  "how-to-calculate-total-addressable-market",
  "tam-vs-sam-vs-som-differences",
  "why-market-size-matters-pitch-decks",
  "top-down-vs-bottom-up-market-sizing",
  "how-to-present-market-size-pitch-deck",
  "what-investors-look-for-tam-sam-som",
  "how-big-market-for-venture-capital",
  "common-market-sizing-mistakes-pitch-decks",
  "examples-great-market-size-slides",
  "estimate-market-size-without-industry-reports",
  "best-data-sources-market-sizing",
  "bottom-up-market-sizing-customer-segments",
  "estimating-market-size-b2b-vs-b2c",
  "how-pricing-affects-tam-calculations",
  "how-expanding-market-increases-tam",
  "startups-grow-niche-markets-to-large-tam",
  "when-small-market-good-strategy",
  "market-segmentation-strategies-startups",
  "product-market-fit-vs-market-size",
]);

const FOUNDER_STRATEGY_SLUGS = new Set([
  "funding-assistance-canadian-startups",
  "soc2-type2-compliance-vanta",
  "fall-in-love-with-a-problem-not-a-solution",
  "what-is-product-market-fit-pmf",
  "software-for-early-stage-startups",
]);

export function getCategoryForPost(slug: string): string | null {
  if (STARTUP_FINANCE_SLUGS.has(slug)) return "startup-finance";
  if (GROWTH_METRICS_SLUGS.has(slug)) return "growth-metrics";
  if (MARKET_RESEARCH_SLUGS.has(slug)) return "market-research";
  if (FOUNDER_STRATEGY_SLUGS.has(slug)) return "founder-strategy";
  return null;
}

export function getPostsByCategory(posts: BlogPost[], categorySlug: string): BlogPost[] {
  return posts.filter((post) => getCategoryForPost(post.slug) === categorySlug);
}

export function getCategoryBySlug(slug: string): BlogCategory | undefined {
  return BLOG_CATEGORIES.find((c) => c.slug === slug);
}

export function searchPosts(posts: BlogPost[], query: string): BlogPost[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return posts;
  return posts.filter(
    (post) =>
      post.title.toLowerCase().includes(lower) ||
      post.excerpt.toLowerCase().includes(lower)
  );
}

export interface RelatedCalculator {
  title: string;
  href: string;
  description: string;
}

const CATEGORY_CALCULATORS: Record<string, RelatedCalculator[]> = {
  "startup-finance": [
    { title: "Burn Rate Calculator", href: "/calculators/burn-rate", description: "Calculate your monthly cash consumption" },
    { title: "Startup Runway Calculator", href: "/calculators/startup-runway", description: "Estimate how long your cash will last" },
    { title: "Startup Valuation Calculator", href: "/calculators/startup-valuation", description: "Estimate your pre-money valuation" },
    { title: "Dilution Calculator", href: "/calculators/dilution", description: "Model equity dilution across funding rounds" },
  ],
  "growth-metrics": [
    { title: "LTV/CAC Calculator", href: "/calculators/ltv-cac", description: "Measure customer unit economics" },
    { title: "Churn Rate Calculator", href: "/calculators/churn-rate", description: "Calculate monthly and annual churn" },
    { title: "CAC Payback Calculator", href: "/calculators/cac-payback", description: "Estimate time to recover acquisition costs" },
    { title: "MRR Growth Calculator", href: "/calculators/mrr-growth", description: "Track monthly recurring revenue growth" },
  ],
  "market-research": [
    { title: "TAM/SAM/SOM Calculator", href: "/calculators/tam-sam-som", description: "Size your addressable market" },
    { title: "Startup Valuation Calculator", href: "/calculators/startup-valuation", description: "Estimate your startup valuation" },
    { title: "Break-Even Calculator", href: "/calculators/break-even", description: "Find your break-even point" },
  ],
  "founder-strategy": [
    { title: "Burn Rate Calculator", href: "/calculators/burn-rate", description: "Calculate your monthly cash consumption" },
    { title: "Startup Runway Calculator", href: "/calculators/startup-runway", description: "Estimate how long your cash will last" },
    { title: "Dilution Calculator", href: "/calculators/dilution", description: "Model equity dilution across funding rounds" },
    { title: "TAM/SAM/SOM Calculator", href: "/calculators/tam-sam-som", description: "Size your addressable market" },
  ],
};

export function getRelatedArticles(
  currentSlug: string,
  allPosts: BlogPost[],
  maxArticles: number = 4
): BlogPost[] {
  const category = getCategoryForPost(currentSlug);
  if (!category) {
    return allPosts
      .filter((p) => p.slug !== currentSlug)
      .slice(0, maxArticles);
  }

  const sameCategoryPosts = allPosts.filter(
    (p) => p.slug !== currentSlug && getCategoryForPost(p.slug) === category
  );

  const shuffled = [...sameCategoryPosts].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, maxArticles);
}

export function getRelatedCalculators(
  currentSlug: string,
  maxCalcs: number = 2
): RelatedCalculator[] {
  const category = getCategoryForPost(currentSlug);
  if (!category) return [];
  const calcs = CATEGORY_CALCULATORS[category] || [];
  return calcs.slice(0, maxCalcs);
}
